/***************************************************************************************************

 Copyright 2023-2024 Fannie Mae. All rights reserved.
 This software contains confidential information and trade secrets of Fannie Mae.
 Use, disclosure, or reproduction is prohibited without the prior written consent of Fannie Mae.

 ****************************************************************************************************/

import { Observable, throwError as observableThrowError, throwError, timer } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { ApiService } from './api.service';
import { EndpointsService } from "./endpoints.service";
import { catchError, map, mergeMap, retryWhen, tap } from 'rxjs/operators';
import { SelectedReports } from '../model/AcquisitionDetails';
import { SelectedInsrReports } from '../model/InsuranceAcquisitionDetails';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  url: string;
  username: string;
  ssn: string;
  org: string;
  body: () => string;

  constructor(private httpClient: HttpClient, private apiService: ApiService, private endpointsService: EndpointsService) {
  }

  public getSubmittedFilesData(userId: string, dealId: string, reportType: string, cfmlycode: string, parentSSNList: string, userType: string): Observable<any> {
    this.url = this.endpointsService.setApiPath('dd') + this.endpointsService.getEndpoints().getSubmittedFilesData + "?userid=" + userId + "&dealid=" + dealId +
      "&reporttype=" + reportType + "&cfmlycode=" + cfmlycode + "&parentSSNList=" + parentSSNList + "&userType=" + userType;
    return this.apiService.sendGetRequest(this.url);
  }

  public getSubmittedInsuranceFilesData(userId: string, dealId: string, reportType: string, cfmlycode: string, parentSSNList: string, userType: string): Observable<any> {
    this.url = this.endpointsService.setApiPath('dd') + this.endpointsService.getEndpoints().getSubmittedInsuranceFilesData + "?userid=" + userId + "&dealid=" + dealId +
      "&reporttype=" + reportType + "&cfmlycode=" + cfmlycode + "&parentSSNList=" + parentSSNList + "&userType=" + userType;
    return this.apiService.sendGetRequest(this.url);
  }

  public getAcquisitionData(selrsrvrnolist: string, startDate: string, endDate: string, isInternal: boolean): Observable<any> {
    this.url = this.endpointsService.setApiPath('dd') + this.endpointsService.getEndpoints().getAcquisitionDashboardList;

    let params = new HttpParams()
      .set('selrsrvrnolist', selrsrvrnolist)
      .set('startPeriod', startDate)
      .set('endPeriod', endDate)
      .set('isInternal', isInternal);

    return this.httpClient.get(this.url, { params: params }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  public getAcqInsuranceData(selrsrvrnolist: string, startDate: string, endDate: string, isInternal: boolean): Observable<any> {
    this.url = this.endpointsService.setApiPath('dd') + this.endpointsService.getEndpoints().getAcqInsuranceDashboardList;

    let params = new HttpParams()
      .set('selrsrvrnolist', selrsrvrnolist)
      .set('startPeriod', startDate)
      .set('endPeriod', endDate)
      .set('isInternal', isInternal);

    return this.httpClient.get(this.url, { params: params }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  // Add the new string to input string if it is not already there
  private addToString(inStr: string, addStr: string): string {
    let newStr = '';
    if (inStr.includes(addStr)) {
      newStr = inStr;
    } else if (inStr.length > 0) {
      newStr = inStr + ',' + addStr;
    } else {
      newStr = addStr;
    }

    return newStr
  }

  // Get the first 5 digits of SSN and create a new string with them
  private getFiveDigitSSN(selrsrvrnolist: string): string {
    const ssnArray = selrsrvrnolist.split(',');
    let newSsnList = '';

    for (let i = 0; i < ssnArray.length; i++) {
      let tStr = ssnArray[i].trim();

      if (tStr.length > 5) {
        newSsnList = this.addToString(newSsnList, tStr.substring(0, 5));
      } else {
        newSsnList = this.addToString(newSsnList, tStr);
      }
    }
    return newSsnList;
  }

  public getLenderActionRequiredData(selrsrvrnolist: string) {
    this.url = this.endpointsService.setApiPath('dd') + this.endpointsService.getEndpoints().getLenderActionRequired;

    let params = new HttpParams()
      .set('selrSrvrNolist', this.getFiveDigitSSN(selrsrvrnolist))

    return this.httpClient.get(this.url, { params: params }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  public getLenderInsuranceActionRequiredData(selrsrvrnolist: string) {
    this.url = this.endpointsService.setApiPath('dd') + this.endpointsService.getEndpoints().getLenderInsuranceActionRequired;


    let params = new HttpParams()
      .set('selrSrvrNolist', this.getFiveDigitSSN(selrsrvrnolist))

    return this.httpClient.get(this.url, { params: params }).pipe(
      catchError(err => this.handleError(err))
    );
  }

  public getUserInformation() {
    let userInfo = [
      {
        "name": this.apiService.getUserDetails().firstName + " " + this.apiService.getUserDetails().lastName,
        "ssn": this.apiService.getSellerServicerNumber().sellerServicerNo,
        "org": this.apiService.getUserDetails().subscriberName
      }
    ];
    return userInfo;
  }

  public getFilesData(objectKey: string, userId: string, cfmlycode: string, validationonly: boolean, userType: string, propertyName: string, dusDealId: string, coltRefNo: string, aqsnDealId: string, propMstrNo: string, transType: string, ssn: string, propId: string, cmmtNo: string, fmFederationID: string): Observable<any> {
    const fileStatusArrayValidation = ['Not acceptable', 'Ready to submit', 'Acceptable with warnings', 'Error'];
    const fileStatusArraySubmission = ['Not acceptable', 'Accepted', 'Accepted with warnings', 'Acceptable with warnings', 'Error', 'Ready to submit'];
    let cnt = 5;
    let obs: Observable<any>;

    let params = new HttpParams()
      .set('userid', userId)
      .set('usertype', userType)
      .set('username', this.apiService.getUserDetails().firstName + " " + this.apiService.getUserDetails().lastName)
      .set('email', this.apiService.getUserDetails().mail)
      .set('org', this.apiService.getUserDetails().subscriberName)
      .set('selrsrvrnolist', this.apiService.getSellerServicerNumber().sellerServicerNo)
      .set('objectkey', objectKey)
      .set('cfmlycode', cfmlycode)
      .set('validationonly', validationonly.toString())
      .set('version', "2")
      .set('propertyname', propertyName)
      .set('dusdealid', dusDealId)
      .set('coltrefno', coltRefNo)
      .set('aqsndealid', aqsnDealId)
      .set('propmstrno', propMstrNo)
      .set('transtype', transType)
      .set('ssn', ssn)
      .set('propid', propId)
      .set('cmmtno', cmmtNo)
      .set('feduserid', fmFederationID);

    this.url = this.endpointsService.setApiPath('dd') + this.endpointsService.getEndpoints().getFileStatus;

    if (validationonly) {
      obs = this.httpClient.get(this.url, { params: params }).pipe(
        map((res: any) => {
          if (fileStatusArrayValidation.includes(res.fileStatus.status)) {
            return res;
          } else {
            throw res;
          }
        }),
        retryWhen(
          mergeMap((error, cnt) => {
            const retryCnt = cnt + 1;
            if (retryCnt > 3) {
              return throwError(() => error);
            }
            return timer(2000);
          })
        )
      );
    } else {
      obs = this.httpClient.get(this.url, { params: params }).pipe(
        map((res: any) => {
          if (fileStatusArraySubmission.includes(res.fileStatus.status)) {
            return res;
          } else {
            throw res;
          }
        })
      );
    }

    return obs;
  }

  public setAcquisitionReportFlags(changedReportArrayDisplay: SelectedReports[]): Observable<any> {
    let obs: Observable<any>;

    let url = this.endpointsService.setApiPath('dd') + this.endpointsService.getEndpoints().setAcquisitionReportFlags;
    let localHeader = new HttpHeaders({ 'content-type': 'application/json' });

    // Filter userData to include only the specified fields
    let userData = changedReportArrayDisplay.map(report => ({
      id: report.dusDealId,
      propNme: report.propNme,
      pca: report.pca,
      esa: report.esa,
      sra: report.sra,
      zon: report.zon,
      appr: report.appr
    }));

    obs = this.httpClient.post(url, userData, { headers: localHeader }).pipe(
      map((res: any) => {
        return res;
      })
    ).pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
    return obs;
  }

  public setAcquisitionInsuranceReportFlags(changedReportArrayDisplay: SelectedInsrReports[]): Observable<any> {
    let obs: Observable<any>;

    let url = this.endpointsService.setApiPath('dd') + this.endpointsService.getEndpoints().setAcquisitionReportFlags;
    let localHeader = new HttpHeaders({ 'content-type': 'application/json' });

    // Filter userData to include only the specified fields
    let userData = changedReportArrayDisplay.map(report => ({
      id: report.dusDealId,
      propNme: report.propNme,
      insr: report.insr,
      a25: report.propAccord25Typ,
      a28: report.propAccord28Typ,
      amLandTtl: report.propAmrcnLandTtlAsscTyp,
      fldAnlysWksh: report.propFldAnlysWkshTyp,
      nfip: report.propNfipTyp,
      prvtOrExcsFldInsrCvrg: report.propPrvtOrExcsFldInsrCvrgTyp,
      spclFldHzrdDtmnForm: report.propSpclFldHzrdDtmnFormTyp,
      schOfVal: report.propSchOfValTyp,
      insrTyp: report.propInsrTyp,
      liabPlcy: report.propLiabPlcyTyp

    }));

    obs = this.httpClient.post(url, userData, { headers: localHeader }).pipe(
      map((res: any) => {
        return res;
      })
    ).pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
    return obs;
  }

  public handleError(error: Response) {
    return observableThrowError(() => 'Server error - is the REST server running?');
  }

  public processFileUpload(event: any): Observable<any> {
    let obs: Observable<any>;

    let url = this.endpointsService.setApiPath('dd') + this.endpointsService.getEndpoints().processFileUpload;
    let localHeader = new HttpHeaders({ 'content-type': 'application/json' });

    obs = this.httpClient.post(url, event, { headers: localHeader }).pipe(
      map((res: any) => {
        return res;
      })
    ).pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
    return obs;
  }


  public getDocwayPreAuth(url: string, params: HttpParams): Observable<any> {
    let obs: Observable<any>;

    obs = this.httpClient.get(url, { params: params, observe: 'response', responseType: 'text' }).pipe(
      map((res: HttpResponse<any>) => {
        return res.body;
      })
    ).pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
    return obs;
  }

}
